import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Film, UserPlus, Settings, Play, Zap, LayoutDashboard, ChevronRight,
  Sparkles, Star, Bookmark, Users, Clock, Timer, Heart, X, Check,
  Globe, Tv, Search, Shield
} from "lucide-react";
import ayerisLogo from "@/assets/ayeris-logo.png";

/* ─── Slide data ─── */
const DEMO_STEP_IDS = [
  { id: "welcome", icon: Film, color: "from-primary to-primary/60" },
  { id: "signup", icon: UserPlus, color: "from-sky-500 to-cyan-500", demo: "signup" },
  { id: "onboarding", icon: Settings, color: "from-amber-500 to-orange-500", demo: "onboarding" },
  { id: "discover-overview", icon: Play, color: "from-emerald-500 to-teal-500", demo: "discover-overview" },
  { id: "discover-mechanics", icon: Zap, color: "from-violet-500 to-purple-500", demo: "discover-mechanics" },
  { id: "home-features", icon: LayoutDashboard, color: "from-rose-500 to-pink-500", demo: "home-features" },
  { id: "closing", icon: Sparkles, color: "from-primary to-primary/60", demo: "closing" },
];

/* ─── Demo: Signup ─── */
const DemoSignup = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 600),
      setTimeout(() => setStep(2), 1200),
      setTimeout(() => setStep(3), 1800),
      setTimeout(() => setStep(4), 2400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const fields = [
    { label: t("demo.signup.usernameField"), value: t("demo.signup.usernameValue"), icon: "👤" },
    { label: t("demo.signup.emailField"), value: t("demo.signup.emailValue"), icon: "✉️" },
    { label: t("demo.signup.dobField"), value: t("demo.signup.dobValue"), icon: "🎂" },
  ];

  return (
    <div className="w-full max-w-[240px] mx-auto space-y-2.5">
      {fields.map((field, i) => (
        <motion.div
          key={field.label}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: step > i ? 1 : 0.3, y: step > i ? 0 : 12 }}
          transition={{ duration: 0.4 }}
          className="rounded-xl bg-secondary/80 border border-border/50 px-3 py-2.5 flex items-center gap-2.5"
        >
          <span className="text-sm">{field.icon}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{field.label}</p>
            <p className="text-xs font-medium text-foreground truncate">
              {step > i ? field.value : ""}
              {step === i + 1 && <span className="animate-pulse text-primary">|</span>}
            </p>
          </div>
          {step > i + 1 && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
        </motion.div>
      ))}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: step >= 4 ? 1 : 0 }}
        className="flex items-center justify-center gap-1.5 pt-1"
      >
        <Shield className="w-3.5 h-3.5 text-primary" />
        <span className="text-[10px] text-muted-foreground">{t("demo.signup.ageNote")}</span>
      </motion.div>
    </div>
  );
};

/* ─── Demo: Onboarding ─── */
const DemoOnboarding = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 800),
      setTimeout(() => setPhase(2), 2000),
      setTimeout(() => setPhase(3), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const providers = ["Netflix", "Disney+", "Prime", "Apple TV+"];
  const favorites = ["🎬 Inception", "📺 Breaking Bad", "🎬 The Matrix", "📺 Stranger Things"];

  return (
    <div className="w-full max-w-[260px] mx-auto space-y-3">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: phase >= 1 ? 1 : 0.3, scale: phase >= 1 ? 1 : 0.9 }}
        className="rounded-xl bg-secondary/80 border border-border/50 px-3 py-2 flex items-center gap-2"
      >
        <Globe className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-foreground">{t("demo.onboarding.regionLabel")}</span>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 ? 1 : 0 }}
          className="text-xs text-primary font-semibold ml-auto"
        >
          {t("demo.onboarding.regionValue")}
        </motion.span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase >= 2 ? 1 : 0.2 }}
        className="space-y-1.5"
      >
        <p className="text-[9px] uppercase tracking-wider text-muted-foreground px-1">{t("demo.onboarding.streamingServices")}</p>
        <div className="flex flex-wrap gap-1.5">
          {providers.map((p, i) => (
            <motion.span
              key={p}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, scale: phase >= 2 ? 1 : 0.8 }}
              transition={{ delay: i * 0.12 }}
              className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-primary/15 text-primary border border-primary/30"
            >
              {p}
            </motion.span>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase >= 3 ? 1 : 0.2 }}
        className="space-y-1.5"
      >
        <p className="text-[9px] uppercase tracking-wider text-muted-foreground px-1">{t("demo.onboarding.favouritePicks")}</p>
        <div className="grid grid-cols-2 gap-1.5">
          {favorites.map((f, i) => (
            <motion.div
              key={f}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: phase >= 3 ? 1 : 0, x: phase >= 3 ? 0 : -8 }}
              transition={{ delay: i * 0.1 }}
              className="px-2 py-1.5 rounded-lg bg-secondary border border-border/50 text-[10px] text-foreground truncate"
            >
              {f}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

/* ─── Demo: Discover Overview ─── */
const DemoDiscoverOverview = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setActive((a) => (a + 1) % 3), 2000);
    return () => clearInterval(interval);
  }, []);

  const modes = [
    { icon: "🎯", label: t("demo.discoverOverview.soloQueue"), desc: t("demo.discoverOverview.justYou") },
    { icon: "👥", label: t("demo.discoverOverview.groupSession"), desc: t("demo.discoverOverview.withFriends") },
    { icon: "➕", label: t("demo.discoverOverview.addFriends"), desc: t("demo.discoverOverview.quickInvites") },
  ];

  const filters = [t("demo.discoverOverview.genre"), t("demo.discoverOverview.decade"), t("demo.discoverOverview.runtime"), t("demo.discoverOverview.tvMovie")];

  return (
    <div className="w-full max-w-[260px] mx-auto space-y-3">
      <div className="flex gap-2">
        {modes.map((m, i) => (
          <motion.div
            key={m.label}
            animate={{
              scale: active === i ? 1.05 : 1,
              borderColor: active === i ? "hsl(231 99% 62% / 0.5)" : "hsl(220 12% 20% / 0.5)",
            }}
            className="flex-1 rounded-xl bg-secondary/80 border px-2 py-2.5 flex flex-col items-center gap-1 cursor-default"
          >
            <span className="text-lg">{m.icon}</span>
            <span className="text-[9px] font-semibold text-foreground">{m.label}</span>
            <span className="text-[8px] text-muted-foreground">{m.desc}</span>
          </motion.div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 justify-center">
        {filters.map((f, i) => (
          <motion.span
            key={f}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 + i * 0.15 }}
            className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-secondary border border-border/50 text-muted-foreground flex items-center gap-1"
          >
            <Search className="w-2.5 h-2.5" /> {f}
          </motion.span>
        ))}
      </div>
    </div>
  );
};

/* ─── Demo: Discover Mechanics ─── */
const DemoDiscoverMechanics = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const cycle = () => {
      setPhase(0);
      setTimeout(() => setPhase(1), 600);
      setTimeout(() => setPhase(2), 1800);
      setTimeout(() => setPhase(3), 3000);
      setTimeout(() => setPhase(4), 4200);
    };
    cycle();
    const interval = setInterval(cycle, 5500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full max-w-[260px] mx-auto space-y-3">
      <div className="relative w-full h-32 rounded-xl bg-gradient-to-b from-muted to-secondary border border-border overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
        <div className="absolute bottom-2.5 left-3 right-3">
          <div className="h-2.5 w-20 bg-foreground/20 rounded-full mb-1" />
          <div className="h-2 w-14 bg-muted-foreground/20 rounded-full" />
        </div>
        <AnimatePresence>
          {phase === 1 && (
            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-star/20 flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-star fill-star" />
              <span className="text-[9px] font-bold text-star">{t("demo.discoverMechanics.picked")}</span>
            </motion.div>
          )}
          {phase === 2 && (
            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-bookmark/20 flex items-center gap-1">
              <Bookmark className="w-3.5 h-3.5 text-bookmark fill-bookmark" />
              <span className="text-[9px] font-bold text-bookmark">{t("demo.discoverMechanics.saved")}</span>
            </motion.div>
          )}
          {phase === 3 && (
            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-primary/20 flex items-center gap-1">
              <span className="text-sm">😍</span>
              <span className="text-[9px] font-bold text-primary">{t("demo.discoverMechanics.lovedIt")}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-center gap-2">
        {[
          { icon: <Star className="w-3.5 h-3.5" />, label: t("demo.discoverMechanics.pick"), active: phase === 1, color: "text-star" },
          { icon: <Bookmark className="w-3.5 h-3.5" />, label: t("demo.discoverMechanics.save"), active: phase === 2, color: "text-bookmark" },
          { icon: <span className="text-xs">😍</span>, label: t("demo.discoverMechanics.rate"), active: phase === 3, color: "text-primary" },
          { icon: <X className="w-3.5 h-3.5" />, label: t("demo.discoverMechanics.skipAction"), active: false, color: "text-destructive" },
        ].map((btn) => (
          <motion.div
            key={btn.label}
            animate={{ scale: btn.active ? 1.15 : 1 }}
            className={`flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-xl bg-secondary/80 border border-border/50 ${btn.active ? "ring-1 ring-primary/40" : ""}`}
          >
            <span className={btn.color}>{btn.icon}</span>
            <span className="text-[8px] text-muted-foreground">{btn.label}</span>
          </motion.div>
        ))}
      </div>

      <motion.div animate={{ opacity: phase === 4 ? 1 : 0.5 }} className="flex items-center justify-between px-2">
        <div className="flex items-center gap-1.5">
          <Timer className="w-3.5 h-3.5 text-destructive" />
          <span className="text-[10px] text-muted-foreground">{t("demo.discoverMechanics.timerQuestion")}</span>
        </div>
        <span className="text-[10px] font-semibold text-primary">{t("demo.discoverMechanics.wePickForYou")}</span>
      </motion.div>
    </div>
  );
};

/* ─── Demo: Home Features ─── */
const DemoHomeFeatures = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const timers = [
      setTimeout(() => setActive(0), 500),
      setTimeout(() => setActive(1), 1500),
      setTimeout(() => setActive(2), 2500),
      setTimeout(() => setActive(3), 3500),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const sections = [
    { emoji: "🎬", label: t("demo.homeFeatures.lastSuggested"), desc: t("demo.homeFeatures.readyToReview"), color: "bg-primary/10 border-primary/30" },
    { emoji: "👀", label: t("demo.homeFeatures.watchHistory"), desc: t("demo.homeFeatures.titlesRated"), color: "bg-emerald-500/10 border-emerald-500/30" },
    { emoji: "🔖", label: t("demo.homeFeatures.bookmarks"), desc: t("demo.homeFeatures.savedForLater"), color: "bg-bookmark/10 border-bookmark/30" },
    { emoji: "🔄", label: t("demo.homeFeatures.bookmarksInQueue"), desc: t("demo.homeFeatures.toggleOnOff"), color: "bg-violet-500/10 border-violet-500/30" },
  ];

  return (
    <div className="w-full max-w-[240px] mx-auto space-y-2">
      {sections.map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: active >= i ? 1 : 0.2, x: active >= i ? 0 : -16 }}
          transition={{ duration: 0.4 }}
          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border ${active >= i ? s.color : "bg-secondary/50 border-border/30"}`}
        >
          <span className="text-base">{s.emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-foreground">{s.label}</p>
            <p className="text-[9px] text-muted-foreground">{s.desc}</p>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </motion.div>
      ))}
    </div>
  );
};

/* ─── Demo: Closing ─── */
const DemoClosing = () => {
  const { t } = useTranslation();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => (t + 1) % 4), 1200);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    t("demo.closing.step1"),
    t("demo.closing.step2"),
    t("demo.closing.step3"),
    t("demo.closing.step4"),
  ];

  return (
    <div className="w-full max-w-[220px] mx-auto space-y-2">
      {steps.map((s, i) => (
        <motion.div
          key={s}
          animate={{
            opacity: tick >= i ? 1 : 0.25,
            scale: tick === i ? 1.05 : 1,
            x: tick >= i ? 0 : -8,
          }}
          transition={{ duration: 0.3 }}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors ${
            tick === i ? "bg-primary/10 border-primary/30" : "bg-secondary/50 border-border/30"
          }`}
        >
          <span className="text-xs font-medium text-foreground">{s}</span>
        </motion.div>
      ))}
    </div>
  );
};

/* ─── Demo renderers map ─── */
const DEMO_COMPONENTS: Record<string, React.FC> = {
  signup: DemoSignup,
  onboarding: DemoOnboarding,
  "discover-overview": DemoDiscoverOverview,
  "discover-mechanics": DemoDiscoverMechanics,
  "home-features": DemoHomeFeatures,
  closing: DemoClosing,
};

/* ─── i18n key map for slide text ─── */
const SLIDE_TEXT_MAP: Record<string, { title: string; subtitle: string; description: string }> = {
  welcome: { title: "demo.welcome.title", subtitle: "demo.welcome.subtitle", description: "demo.welcome.description" },
  signup: { title: "demo.signup.title", subtitle: "demo.signup.subtitle", description: "demo.signup.description" },
  onboarding: { title: "demo.onboarding.title", subtitle: "demo.onboarding.subtitle", description: "demo.onboarding.description" },
  "discover-overview": { title: "demo.discoverOverview.title", subtitle: "demo.discoverOverview.subtitle", description: "demo.discoverOverview.description" },
  "discover-mechanics": { title: "demo.discoverMechanics.title", subtitle: "demo.discoverMechanics.subtitle", description: "demo.discoverMechanics.description" },
  "home-features": { title: "demo.homeFeatures.title", subtitle: "demo.homeFeatures.subtitle", description: "demo.homeFeatures.description" },
  closing: { title: "demo.closing.title", subtitle: "demo.closing.subtitle", description: "demo.closing.description" },
};

/* ─── Main Demo page ─── */
const Demo = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);

  const currentStep = DEMO_STEP_IDS[step];
  const isLast = step === DEMO_STEP_IDS.length - 1;
  const textKeys = SLIDE_TEXT_MAP[currentStep.id];

  useEffect(() => {
    if (!autoAdvance || isLast) return;
    const timer = setTimeout(() => setStep((s) => s + 1), 10000);
    return () => clearTimeout(timer);
  }, [step, autoAdvance, isLast]);

  const goNext = useCallback(() => {
    setAutoAdvance(false);
    if (isLast) {
      navigate("/demo-end");
    } else {
      setStep((s) => s + 1);
    }
  }, [isLast, navigate]);

  const goPrev = useCallback(() => {
    setAutoAdvance(false);
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleSkip = () => navigate("/demo-end");

  const DemoComponent = currentStep.demo ? DEMO_COMPONENTS[currentStep.demo] : null;

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-4 py-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20"
          style={{ background: "radial-gradient(ellipse, hsl(231 99% 62% / 0.4), transparent 70%)" }}
        />
      </div>

      <div className="absolute top-4 left-4 right-4 z-20 flex justify-between">
        <button
          onClick={() => navigate("/landing")}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full bg-secondary/50 border border-border/50"
        >
          ←
        </button>
        <button
          onClick={handleSkip}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full bg-secondary/50 border border-border/50"
        >
          {t("demo.skip")}
        </button>
      </div>

      <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
        <div className="flex gap-1.5 mb-6">
          {DEMO_STEP_IDS.map((_, i) => (
            <button
              key={i}
              onClick={() => { setAutoAdvance(false); setStep(i); }}
              className="relative h-1.5 rounded-full transition-all duration-300"
              style={{ width: i === step ? 24 : 8 }}
            >
              <div
                className={`absolute inset-0 rounded-full transition-colors duration-300 ${
                  i === step ? "bg-primary" : i < step ? "bg-primary/40" : "bg-border"
                }`}
              />
              {i === step && autoAdvance && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 10, ease: "linear" }}
                  className="absolute inset-0 rounded-full bg-primary origin-left"
                  key={`progress-${step}`}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="w-full text-center space-y-4"
          >
            {currentStep.id === "welcome" ? (
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              >
                <img src={ayerisLogo} alt="Ayeris" className="w-24 h-24 rounded-3xl shadow-iris mx-auto" />
              </motion.div>
            ) : (
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${currentStep.color} shadow-lg mx-auto flex items-center justify-center`}
              >
                <currentStep.icon className="w-7 h-7 text-primary-foreground" />
              </motion.div>
            )}

            <div className="space-y-1.5">
              <h2 className="font-display text-2xl font-bold text-foreground">
                {t(textKeys.title)}
              </h2>
              <p className="font-display text-base font-semibold text-primary">
                {t(textKeys.subtitle)}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-[320px] mx-auto">
                {t(textKeys.description)}
              </p>
            </div>

            {DemoComponent && (
              <div className="py-8 min-h-[240px] flex items-center justify-center">
                <DemoComponent />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center gap-3 w-full mt-12">
          {step > 0 && (
            <button
              onClick={goPrev}
              className="px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm font-medium active:scale-95 transition-transform"
            >
              {t("demo.back")}
            </button>
          )}
          <button
            onClick={goNext}
            className="flex-1 py-3.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"
          >
            {isLast ? (
              <>
                <Sparkles className="w-4 h-4" /> {t("demo.letsGo")}
              </>
            ) : (
              <>
                {t("demo.next")} <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Demo;
